import React from "react";
import style from "./markdownDisplay.module.css";
import ReactMarkdown from "react-markdown";
import gfm from "remark-gfm";
// import rehypeHighlight from 'rehype-highlight'
import rehypeRaw from "rehype-raw";

import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark as codeStyle } from "react-syntax-highlighter/dist/esm/styles/prism";

import { ImageWithAuth } from "./components/ImageWithAuth";

interface Props {
  value: string;
  header?: boolean;
}

function MarkdownDisplay({ value, header }: Readonly<Props>) {
  const markdownContent = header ? `### ${value}` : `${value}`;

  return (
    <ReactMarkdown
      remarkPlugins={[gfm]}
      rehypePlugins={[rehypeRaw]}
      className={style.markdownDiv}
      components={{
        img: React.useCallback(({ alt, src, title }: { alt?: string; src?: string; title?: string }) => (
          <ImageWithAuth 
            alt={alt} 
            src={src} 
            title={title} 
            style={{ maxWidth: "100%", maxHeight: 562, margin: "10px" }} 
          />
        ), []),
        code: ({ inline, className, children, ...props }: { inline?: boolean; className?: string; children?: React.ReactNode }) => {
          const match = /language-(\w+)/.exec(className || "");
          
          if (inline || (typeof children === 'string' && children.split('\n').length === 1)) {
            return (
              <code
                className={style.inlineCode}
                {...props}
              >
                {children}
              </code>
            );
          }

          return (
            <SyntaxHighlighter
              style={codeStyle}
              showLineNumbers
              lineProps={{
                style: {
                  wordBreak: "break-word",
                  whiteSpace: "pre-wrap",
                  paddingLeft: "3em" /* Adjust this value as needed */,
                  textIndent: "-4.5em",
                  display: "block" /* Ensure each line is a block element */,
                },
              }}
              lineNumberStyle={{
                minWidth: "2em", // Ensure space for line numbers
                textAlign: "right", // Right align line numbers
                paddingRight: "1em", // Space between line numbers and code
                userSelect: "none",
              }}
              wrapLines
              PreTag="pre"
              language={match ? match[1] : "http"}
              {...props}
            >
              {String(children).replace(/\n$/, "")}
            </SyntaxHighlighter>
          )
        },
      }}
    >
      {markdownContent}
    </ReactMarkdown>
  );
}

export default MarkdownDisplay;
